import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { AdminCourseModeStatus } from '@edtech-ui/constants/admin-course-mode';
import { CreateQuizPayload } from '@edtech-ui/core/models/quiz-creation/create-quiz';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { CompanyService } from '@edtech-ui/core/services/company-management/company.service';
import { RecruitmentJobService } from '@edtech-ui/core/services/recruitment-management/recruitment-job.service';
import { GetAllCompany } from '@edtech-ui/core/models/company-management/get-all-companies-list';
import { CompanyJob } from '@edtech-ui/core/models/recruitment/get-all-job-listing';
import { quizNameValidator } from '@edtech-ui/core/utils/admin-quiz-validation/admin-create-quiz-validators';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { MockTestDescriptionValidator } from '@edtech-ui/core/services/mock-assessment-validation';

@Component({
	selector: 'app-add-quiz-dialog-box',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, FormsModule, MatProgressSpinnerModule, HasPermissionDirective],
	templateUrl: './add-quiz-dialog-box.component.html',
	styleUrl: './add-quiz-dialog-box.component.scss'
})
export class AddQuizDialogBoxComponent implements OnInit, OnDestroy {

	mode: string = 'course';
	isGroupDropdownOpen = false;
	selectedGroups: string[] = [];
	selectedOptions: string[] = [];
	@Output() modeSelected = new EventEmitter<string>();
	quizForm: FormGroup;
	classroomId = environment.classroomId;
	loading = false;
	page = 1;
	limit = 10;
	totalRecords = 0;
	totalPages = 0;
	visiblePages: number[] = [];
	totalQuiz: number = 0;
	pageCourse = 1;
	limitCourse = 10;
	totalPagesCourse = 0;
	courses: { id: string; name: string }[] = [];
	selectedCourse: { id: string; name: string } | null = null;
	subscription = new Subscription();
	companies: GetAllCompany[] = [];
	isLoadingCompanies: boolean = true;
	jobs: CompanyJob[] = [];
	isLoadingJobs: boolean = false;
	isAdding: boolean = false;
	isPublishedAsMockTest = false;
	PermissionForStudents = PermissionForStudents;


	constructor(
		private dialogRef: MatDialogRef<AddQuizDialogBoxComponent>,
		private router: Router,
		private toastr: ToastrService,
		private _quizService: QuizManagementService,
		private courseService: CourseManagementService,
		private companyService: CompanyService,
		private jobService: RecruitmentJobService
	) {

		this.quizForm = new FormGroup({
			quiz_name: new FormControl('', [Validators.required, quizNameValidator]),
			description: new FormControl('', [MockTestDescriptionValidator]),
			map_to_company: new FormControl(''),
			map_to_job: new FormControl(''),
			time_limit: new FormGroup({
				hours: new FormControl(0, [
					Validators.required,
					Validators.min(0),
					Validators.max(23),
					Validators.pattern(/^\d{1,2}$/)
				]),
				minutes: new FormControl(0, [
					Validators.required,
					Validators.min(0),
					Validators.max(59),
					Validators.pattern(/^\d{1,2}$/)
				]),
				seconds: new FormControl(0, [
					Validators.required,
					Validators.min(0),
					Validators.max(59),
					Validators.pattern(/^\d{1,2}$/)
				])
			}),
			attempts_allowed: new FormControl(null, [Validators.required, Validators.pattern(/^[0-9]{1,3}$/), Validators.min(1)]),
			passing_score: new FormControl(null, [Validators.pattern(/^(100|[1-9][0-9]?)$/)]),
			questions_per_quiz: new FormControl(null, [Validators.required, Validators.min(1), Validators.pattern(/^[1-9][0-9]{0,2}$/)]),
			shuffle_options: new FormControl(false),
			shuffle_questions: new FormControl(false),
			//mode: new FormControl('course', [Validators.required]),
			//course: new FormControl(null),
			//groups: new FormControl([]),
			//start_date_time: new FormControl(null),
			//end_date_time: new FormControl(null),
			//timeZone: new FormControl('IST'),
			isProctored: new FormControl(false),
			proctoring_config: new FormGroup({
				detect_tab_switch: new FormControl(false),
				restrict_new_tabs: new FormControl(false),
				enable_camera: new FormControl(false),
				face_detection: new FormControl(false),
				multiple_person_detection: new FormControl(false),
				no_talking_detection: new FormControl(false),
				eye_movement_tracking: new FormControl(false),
				max_warnings: new FormControl(3)
			})
		});

		this.quizForm.get('mode')?.valueChanges.subscribe(mode => {
			const course = this.quizForm.get('course');
			const groups = this.quizForm.get('groups');

			if (mode === 'course') {
				course?.setValidators([Validators.required]);
				groups?.clearValidators();
			} else {
				groups?.setValidators([Validators.required]);
				course?.clearValidators();
			}

			course?.updateValueAndValidity();
			groups?.updateValueAndValidity();
		});
	}


	ngOnInit(): void {
		this.getAdminCourses();
		this.getCompanies();

		this.quizForm.get('isProctored')?.valueChanges.subscribe(isProctored => {
			this.syncProctoringOptions(isProctored);
		});

		this.quizForm.get('map_to_company')?.valueChanges.subscribe(companyId => {
			const jobControl = this.quizForm.get('map_to_job');
			jobControl?.setValue('');
			this.jobs = [];
			if (companyId) {
				jobControl?.setValidators([Validators.required]);
				this.getJobsForCompany(companyId);
			} else {
				jobControl?.clearValidators();
			}
			jobControl?.updateValueAndValidity();
		});
	}

	getJobsForCompany(companyId: string) {
		this.isLoadingJobs = true;
		this.subscription.add(
			this.jobService.getAllJobPosting(companyId, 1, 100, 'active').subscribe({
				next: (res) => {
					this.isLoadingJobs = false;
					if (res.Success) {
						// Only show active (approved) jobs
						this.jobs = res.Data.jobs.filter(job => job.status === 'active');
					}
				},
				error: (err) => {
					this.isLoadingJobs = false;
					this.toastr.error('Failed to fetch jobs');
				}
			})
		);
	}

	getCompanies() {
		this.isLoadingCompanies = true;
		this.subscription.add(
			this.companyService.getAllCompaniesList({ page: 1, limit: 100, status: 'approved' }).subscribe({
				next: (res) => {
					this.isLoadingCompanies = false;
					if (res.Success) {
						this.companies = res.Data.companies;
					}
				},
				error: (err) => {
					this.isLoadingCompanies = false;
					this.toastr.error('Failed to fetch companies');
				}
			})
		);
	}


	closeDialog() {
		this.dialogRef.close();
	}


	preventNegative(event: KeyboardEvent): void {
		if (event.key === '-' || event.key === 'e') {
			event.preventDefault();
		}
	}

	userGroups: string[] = [
		'Testing 1',
		'Testing 2',
		'Testing 3',
		'Testing 4',
		'Testing 5'
	];


	toggleGroupDropdown() {
		this.isGroupDropdownOpen = !this.isGroupDropdownOpen;
	}


	onSelectGroup(group: string) {
		const current = this.quizForm.get('groups')?.value || [];

		if (current.includes(group)) {
			const updated = current.filter((g: string) => g !== group);
			this.quizForm.patchValue({ groups: updated });
		} else {
			this.quizForm.patchValue({ groups: [...current, group] });
		}

		this.selectedGroups = this.quizForm.get('groups')?.value;
	}


	toggleOption(option: string) {
		if (this.selectedOptions.includes(option)) {
			this.selectedOptions = this.selectedOptions.filter(o => o !== option);
		} else {
			this.selectedOptions.push(option);
		}
	}


	onModeChange(value: string) {
		this.mode = value;
		this.modeSelected.emit(value);
	}


	createQuiz(): void {
		if (this.quizForm.invalid) {
			this.quizForm.markAllAsTouched();
			return;
		}

		const form = this.quizForm.value;

		// if (form.mode === 'course' && !form.course) {
		// 	this.toastr.error('Please select a course');
		// 	return;
		// }

		// if (form.mode === 'group' && (!form.groups || form.groups.length === 0)) {
		// 	this.toastr.error('Please select at least one group');
		// 	return;
		// }

		const hours = Number(form.time_limit?.hours || 0);
		const minutes = Number(form.time_limit?.minutes || 0);
		const seconds = Number(form.time_limit?.seconds || 0);
		const timeLimit = (hours * 3600) + (minutes * 60) + seconds;

		if (timeLimit === 0) {
			this.toastr.error('Please set a time limit');
			return;
		}

		const proctorForm = this.quizForm.value.proctoring_config;
		const proctoringConfig = {
			enable_proctoring: this.quizForm.value.isProctored,
			detect_tab_switch: proctorForm.detect_tab_switch,
			restrict_new_tabs: proctorForm.restrict_new_tabs,
			enable_camera: proctorForm.enable_camera,
			face_detection: proctorForm.face_detection,
			multiple_person_detection: proctorForm.multiple_person_detection,
			no_talking_detection: proctorForm.no_talking_detection,
			eye_movement_tracking: proctorForm.eye_movement_tracking,
			max_warnings: proctorForm.max_warnings || 3
		};

		this.isAdding = true;

		const payload: CreateQuizPayload = {
			quiz_name: form.quiz_name?.trim(),
			description: form.description || '',
			time_limit: timeLimit,
			course_id: form.mode === 'course' ? form.course : undefined,
			shuffle_questions: form.shuffle_questions,
			shuffle_options: form.shuffle_options,
			attempts_allowed: this.isPublishedAsMockTest ? null : (form.attempts_allowed !== null && form.attempts_allowed !== '' ? Number(form.attempts_allowed) : null),
			passing_score: form.passing_score || 0,
			questions_per_quiz: form.questions_per_quiz !== null && form.questions_per_quiz !== '' ? Number(form.questions_per_quiz) : null,
			start_date_time: form.start_date_time ? new Date(form.start_date_time).toISOString() : null,
			end_date_time: form.end_date_time ? new Date(form.end_date_time).toISOString() : null,
			timeZone: form.timeZone,
			proctoring_config: proctoringConfig,
			mode: AdminCourseModeStatus.PROCTORED_ASSESSMENT,
			company_id: form.map_to_company || undefined,
			job_id: form.map_to_job || undefined,
			is_public: this.isPublishedAsMockTest
		};

		this.subscription.add(
			this._quizService.postCreateQuiz(payload, this.classroomId).pipe(
				tap(response => {
					if (response.Success) {
						this.router.navigate(['/admin-student-platform/create-quiz', this.classroomId, response.Data.quiz_id],
							{ queryParams: { mode: form.mode } });
						this.closeDialog();
					} else {
						this.toastr.error(response?.Message);
					}
				}),
				finalize(() => {
					this.isAdding = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null)
				}),
			).subscribe()
		);
	}


	onMockTestToggle(isPublic: boolean): void {
		this.isPublishedAsMockTest = isPublic;
		const attemptsControl = this.quizForm.get('attempts_allowed');
		if (isPublic) {
			attemptsControl?.clearValidators();
			attemptsControl?.setValue(null);
		} else {
			attemptsControl?.setValidators([
				Validators.required,
				Validators.pattern(/^[0-9]{1,3}$/),
				Validators.min(1)
			]);
			attemptsControl?.setValue(null);
		}
		attemptsControl?.updateValueAndValidity();
	}


	getAdminCourses(): void {
		if (this.loading) return;
		this.loading = true;

		this.subscription.add(
			this.courseService.getCourses(this.classroomId, this.pageCourse, this.limitCourse, 'created_at', 'desc', 'all').pipe(
				finalize(() => {
					this.loading = false;
				})).subscribe({
					next: res => {
						this.loading = false;
						if (res?.Success && res.Data?.data) {
							const publishedCourses = res.Data.data.filter((course: any) => course.status === 'Published')
								.map((course: any) => ({
									id: course.course_id,
									name: course.course_title,
									status: course.status
								}));
							this.courses = [...this.courses, ...publishedCourses];
							this.pageCourse = res.Data.page + 1;
							this.totalPagesCourse = res.Data.total_pages;
						}
					},
					error: () => {
						this.loading = false;
					}
				})
		);
	}

	get isFormValid(): boolean {
		const form = this.quizForm;
		if (form.invalid) return false;
		const t = form.value.time_limit;
		const totalTime = (t.hours || 0) * 3600 + (t.minutes || 0) * 60 + (t.seconds || 0);
		if (totalTime === 0) return false;
		return true;
	}


	handleTimeInput(event: any, max: number) {
		let value = event.target.value;
		value = value.replace(/[^0-9]/g, '');
		if (value.length > 2) {
			value = value.slice(0, 2);
		}
		if (value !== '' && Number(value) > max) {
			value = max.toString();
		}
		event.target.value = value;
	}


	private syncProctoringOptions(isProctored: boolean): void {
		const proctoringConfig = this.quizForm.get('proctoring_config') as FormGroup;

		if (!proctoringConfig) {
			return;
		}
		const proctoringOptions = [
			'detect_tab_switch',
			'restrict_new_tabs',
			'enable_camera',
			'face_detection',
			'multiple_person_detection',
			'no_talking_detection',
			'eye_movement_tracking'
		];

		proctoringOptions.forEach(option => {
			proctoringConfig.get(option)?.setValue(isProctored, {
				emitEvent: false
			});
		});
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}